import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import * as PrefabManager from '../PrefabManager'

const { getPointer, NULL, pointsToVec2Array } = Box2D; // emscriptem specific

const generateGiphyURL = id => `https://i.giphy.com/media/${id}/giphy.mp4`

class GiphyPlayer extends PrefabManager.basePrefab {
    constructor(target) {
		super(target);
		this.base = this.lookupObject.base;
		this.textureContainer = this.lookupObject.texture;
    }
    init() {
		super.init();

		// Starting a level rebuilds lookupGroups (and recreates some runtime objects),
		// so resolve these references from the current registry instead of relying on
		// the editor-time lookup performed by the constructor.
		this.base = this.lookupObject.base;
		this.textureContainer = this.lookupObject.texture;

		const { giphy_id, loop, autoPlay, autoSize, width, height, isFixed, collisions  } = this.prefabObject.settings;
		if(!giphy_id) return;

		// The prefab's "texture" reference is the container created by
		// buildTextureFromObj. Pixi stores its renderable Sprite explicitly as
		// originalSprite; myTileSprite only exists for vector graphics that use a
		// tiled fill and is therefore absent for GiphyPlayer.
		this.textureSprite = this.textureContainer?.originalSprite;
		if(!this.textureSprite || !('texture' in this.textureSprite)) {
			console.warn('[GiphyPlayer] Texture sprite is unavailable');
			return;
		}

		const initialWidth = this.textureSprite.width;
		const initialHeight = this.textureSprite.height;

		// Pixi 8's Texture.from(string) only looks up an already-loaded cache entry;
		// it no longer creates a video texture from a remote URL. Its asset loader
		// waits for a decodable frame before exposing the video texture to WebGL.
		this.videoActive = true;

		if(autoSize){
			this.textureSprite.width = initialWidth;
			this.textureSprite.height = initialHeight;
		} else {
			this.textureSprite.width = width;
			this.textureSprite.height = height;
		}

		void PIXI.Assets.load({
			src: generateGiphyURL(giphy_id),
			parser: 'video',
			data: {
				autoPlay: Boolean(autoPlay),
				loop: Boolean(loop),
				muted: true,
				playsinline: true,
				crossorigin: 'anonymous',
				preload: true,
			},
		}).then(videoTexture => {
			if(!this.videoActive || this.destroyed || videoTexture?.destroyed || this.textureSprite?.destroyed) return;
			this.videoTexture = videoTexture;
			this.videoSource = videoTexture.source;
			this.videoPlayer = this.videoSource.resource;
			this.videoPlayer.loop = Boolean(loop);
			this.videoPlayer.autoplay = Boolean(autoPlay);
			this.videoPlayer.muted = true;
			this.videoPlayer.playsInline = true;
			this.textureSprite.texture = this.videoTexture;
			this.textureSprite.width = autoSize ? initialWidth : width;
			this.textureSprite.height = autoSize ? initialHeight : height;
			if(autoPlay) this.play();
			else this.pause();
		}).catch(error => {
			if(!this.videoActive) return;
			const mediaError = this.videoPlayer?.error;
			console.warn(
				`[GiphyPlayer] Unable to load Giphy video "${giphy_id}"`,
				mediaError?.message || error,
			);
		});

		if(isFixed){
            this.base.SetType(Box2D.b2_staticBody);
        }else{
            this.base.SetType(Box2D.b2_dynamicBody);
        }

		game.editor.setBodyCollision(this.base, [collisions ? 0 : 2]);
	}
	play(){
		const playPromise = this.videoPlayer?.play();
		playPromise?.catch?.(() => {});
	}

	pause(){
		this.videoPlayer?.pause();
	}

	reset(){
		this.videoActive = false;
		if(this.videoPlayer){
			this.videoPlayer.pause();
			this.videoPlayer.currentTime = 0;
		}
	}

	update(){
		if(!this.videoPlayer) return;
		this.videoPlayer.playbackRate = game.editor.editorSettingsObject.gameSpeed;
	}

	set(property, value) {
		super.set(property, value);
        switch (property) {
			case 'width':
				this.setWidthHeight(value, this.height);
			break
			case 'height':
				this.setWidthHeight(this.width, value);
			break
        }
	}

	setWidthHeight(width, height){

		const body = this.base;

		const aabb = new Box2D.b2AABB();
		aabb.get_lowerBound().Set(Number.MAX_VALUE, Number.MAX_VALUE);
		aabb.get_upperBound().Set(-Number.MAX_VALUE, -Number.MAX_VALUE);

		const oldRot = body.GetAngle();
		body.SetTransform(body.GetPosition(), 0);

		for (let fixture = body.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
			aabb.Combine(fixture.GetAABB(0));
		}

		body.SetTransform(body.GetPosition(), oldRot);

		this.width = width;
		this.height = height;

		var currentSize = {
			width: aabb.GetExtents().x * 2 * game.editor.PTM,
			height: aabb.GetExtents().y * 2 * game.editor.PTM
		}

		Box2D.destroy(aabb);

		let scaleX = width / currentSize.width;
		let scaleY = height / currentSize.height;

		let oldFixtures = []

		for (let fixture = body.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
			fixture.SetSensor(true);
			oldFixtures.push(fixture);
		}

		oldFixtures.reverse();

		for (let i = 0; i < oldFixtures.length; i++) {
			let fixture = oldFixtures[i];
			const baseShape = fixture.GetShape();
			if (baseShape.GetType() === Box2D.b2Shape.e_polygon) {
				const shape = Box2D.castObject(baseShape, Box2D.b2PolygonShape);

				const vertices = [];
				for (let vertexIx = 0; vertexIx < shape.get_m_count(); vertexIx++) {
					const vertex = shape.get_m_vertices(vertexIx);
					vertices.push({x:vertex.get_x()*scaleX, y:vertex.get_y()*scaleY});
				}

				shape.Set(pointsToVec2Array(vertices)[0], vertices.length);

				let oldVertices = body.mySprite.data.vertices[i];

				for (let j = 0; j < oldVertices.length; j++) {
					oldVertices[j].x = oldVertices[j].x * scaleX;
					oldVertices[j].y = oldVertices[j].y * scaleY;
				}

			}
		};

		this.textureContainer.width = width;
		this.textureContainer.height = height;

		body.SetTransform(body.GetPosition(), body.GetAngle());

		game.editor.updateBodyShapes(body);
	}
}

GiphyPlayer.settings = Object.assign({}, GiphyPlayer.settings, {
	"giphy_id": 'IRFQYGCokErS0',
	loop: true,
	autoPlay: true,
	autoSize: true,
	width: 335,
    height: 196,
	"isFixed": true,
	"collisions": false,
});

GiphyPlayer.settingsOptions = Object.assign({}, GiphyPlayer.settingsOptions, {
	"giphy_id": 'IRFQYGCokErS0',
	loop: true,
	autoPlay: true,
	autoSize: true,
	width: {
        min: 0,
        max: 10000.0,
        step: 1
	},
	height: {
        min: 0,
        max: 10000.0,
        step: 1
	},
	"isFixed": true,
	"collisions": false,
});

PrefabManager.prefabLibrary.GiphyPlayer = {
    json: '{"objects":[[0,-0.0167,-0.0166,0,"giphy_player","base",0,["#999999"],["#000"],[1],false,true,[[{"x":-5.5733,"y":3.2567},{"x":-5.5733,"y":-3.2567},{"x":5.5733,"y":-3.2567},{"x":5.5733,"y":3.2567}]],[0.1],[0],[0],"",[0],true,false,false,[0.5],[0.2],false,true,false,false,1],[1,0.5005,0.4995,0,"giphy_player","texture",1,"GiphyPlayer instance 10000",0,1.4142,-0.7844,0,false,"#FFFFFF",1,1,1,0,0,0,true]]}',
    class: GiphyPlayer,
    library: PrefabManager.LIBRARY_MISC,
}
